<template>
    <div>
        <div>{{ startDateLabel }} {{ startDateDisplayString }}</div>
        <br />
        <pc-date
            id="ExpirationDate"
            ref="ExpirationDate"
            v-model="newExpirationDateString"
            v-bind:label="expirationDateLabel"
            class="mb-2" />
        <div>
            {{ expirationDateInfoText }}
        </div>
        <div v-if="showTodayWarning">
            <pc-alert v-bind="alertBinder">
                {{ expirationTodayWarningMessage }}
            </pc-alert>
            <br />
            <pc-checkbox
                id="ConfirmExpirationToday"
                v-model="confirmExpirationToday"
                v-bind:label="confirmExpirationTodayLabel"
                required />
        </div>
        <div v-if="showDateInPastError">
            <pc-alert v-bind="errorBinder">
                {{ expirationInPastErrorMessage }}
            </pc-alert>
        </div>
        <div v-if="showDateNotAfterStartError">
            <pc-alert v-bind="errorBinder">
                {{ expirationBeforeStartErrorMessage }}
            </pc-alert>
        </div>
        <div v-if="showDateInvalidError">
            <pc-alert v-bind="errorBinder">
                {{ expirationInvalidErrorMessage }}
            </pc-alert>
        </div>
    </div>
</template>

<script>
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/setIncentiveDesignExpirationDateDialog.i18n";
    import { ClientDate, dateStrToClientDate } from "Util/clientDate";
    import { isValidDate } from "Validation/validator";
    import { DIALOG_RESULT } from "Components/pc-dialog.vue";
    import { ALERT_SEVERITY } from "Util/alerts";

    export default {
        props: {
            startDateString: { type: String, required: true },
            expirationDateString: { type: String, required: true }
        },
        data() {
            return {
                newExpirationDateString: this.expirationDateString,
                todayDate: this.dateValueFromDateString(new ClientDate(this.$app)),
                confirmExpirationToday: false
            };
        },
        computed: {
            startDateDisplayString() {
                return this.startDateString === "" ? this.noStartDateMessage : this.startDateString;
            },
            startDate() {
                if (this.startDateString) {
                    return this.dateValueFromDateString(dateStrToClientDate(this.$app, this.startDateString));
                } else {
                    // A sufficiently early "beginning of time" date
                    return this.dateValueFromDateString(new ClientDate(this.$app, 2001, 1, 1));
                }
            },
            oldExpirationDate() {
                if (this.expirationDateString) {
                    return this.dateValueFromDateString(dateStrToClientDate(this.$app, this.expirationDateString));
                } else {
                    // A sufficiently distant "end of time" date
                    return this.dateValueFromDateString(new ClientDate(this.$app, 2199, 12, 31));
                }
            },
            newExpirationDate() {
                if (this.newExpirationDateString) {
                    return this.dateValueFromDateString(dateStrToClientDate(this.$app, this.newExpirationDateString));
                } else {
                    // A sufficiently distant "end of time" date
                    return this.dateValueFromDateString(new ClientDate(this.$app, 2199, 12, 31));
                }
            },
            showTodayWarning() {
                // Show the Today warning if the Expiration Date is being *changed* to Today
                return (
                    this.newExpirationDateString &&
                    !this.showDateNotAfterStartError &&
                    this.oldExpirationDate !== this.todayDate &&
                    this.newExpirationDate === this.todayDate
                );
            },
            showDateInPastError() {
                return (
                    this.newExpirationDateString &&
                    isValidDate(this.$app, this.newExpirationDateString) &&
                    this.newExpirationDate < this.todayDate
                );
            },
            showDateNotAfterStartError() {
                return (
                    this.newExpirationDateString &&
                    isValidDate(this.$app, this.newExpirationDateString) &&
                    !this.showDateInPastError &&
                    this.newExpirationDate <= this.startDate
                );
            },
            showDateInvalidError() {
                if (this.newExpirationDateString) {
                    return !isValidDate(this.$app, this.newExpirationDateString);
                }
                return false;
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            ),
            alertBinder() {
                return {
                    shown: true,
                    severity: ALERT_SEVERITY.warning,
                    class: { "mb-0": true, "mt-3": true }
                };
            },
            errorBinder() {
                return {
                    shown: true,
                    severity: ALERT_SEVERITY.error,
                    class: { "mb-0": true, "mt-3": true }
                };
            }
        },
        methods: {
            dateValueFromDateString(dateString) {
                return Date.parse(dateString);
            },
            verifyCanClose(result) {
                if (result === DIALOG_RESULT.cancel) {
                    return true;
                }
                if (this.showTodayWarning) {
                    return this.confirmExpirationToday;
                } else if (this.showDateInPastError || this.showDateNotAfterStartError || this.showDateInvalidError) {
                    return false;
                }
                return true;
            },
            resultHook() {
                return {
                    NewExpirationDateString: this.newExpirationDateString
                };
            }
        }
    };
</script>
